import { Button, Flex } from '@chakra-ui/react';
import { getBrandColor } from 'utils/brandColor';

export interface SegmentedControlOption {
  id: string;
  label: string;
  /** Optional override color when this option is the active one. */
  activeColor?: string;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  activeId: string;
  onChange: (id: string) => void;
  /** Fallback color used when an option doesn't define its own activeColor. */
  defaultActiveColor?: string;
  /** Visual size — "lg" for the top PACKS/ABONNEMENTS, "sm" for sub-tabs. */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Pill-style segmented control: a row of buttons inside a rounded container.
 * The active button uses its own color (or the shared default), the
 * inactive buttons stay neutral. Used twice on the new Tarifs page:
 * once for the top-level PACKS / ABONNEMENTS choice, and once again for
 * the sub-tabs nested under each segment.
 *
 * Visual fidelity is intentionally functional, not pixel-perfect — the
 * priority for Phase 4.5 is the back-office mechanism. Polish can come in
 * a follow-up once the data flow is validated.
 */
export function SegmentedControl({
  options,
  activeId,
  onChange,
  defaultActiveColor = getBrandColor(),
  size = 'md',
}: SegmentedControlProps) {
  if (options.length === 0) return null;

  return (
    <Flex
      display="inline-flex"
      bg="white"
      borderRadius="full"
      borderWidth="1px"
      borderColor="gray.300"
      p={1}
      gap={1}
    >
      {options.map((option) => {
        const isActive = option.id === activeId;
        const activeColor = option.activeColor || defaultActiveColor;
        return (
          <Button
            key={option.id}
            size={size}
            borderRadius="full"
            variant="unstyled"
            px={size === 'lg' ? 6 : size === 'md' ? 5 : 4}
            h={size === 'lg' ? 10 : size === 'md' ? 9 : 8}
            fontSize={size === 'lg' ? 'sm' : 'xs'}
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="wide"
            bg={isActive ? activeColor : 'transparent'}
            color={isActive ? 'white' : 'gray.700'}
            _hover={isActive ? {} : { bg: 'gray.100' }}
            onClick={() => onChange(option.id)}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
}
